import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ExpandMore,
  PlayArrow,
  CheckCircle,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import { ValidationResult } from '../../types/dynamicRouting';

interface ValidationPanelProps {
  validationResult?: ValidationResult;
  isValidating: boolean;
  onValidate: () => void;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({
  validationResult,
  isValidating,
  onValidate,
}) => {
  const getIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'info':
        return <Info color="info" />;
      default:
        return <Info />;
    }
  };

  if (!validationResult && !isValidating) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Validation Results
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click the validate button to check your configuration for errors and warnings.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={onValidate}
          size="large"
        >
          Validate Configuration
        </Button>
      </Box>
    );
  }

  if (isValidating) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Validating Configuration...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we check your configuration.
        </Typography>
      </Box>
    );
  }

  if (!validationResult) {
    return null;
  }

  const { isValid, messages, summary } = validationResult;
  const errorMessages = messages.filter(msg => msg.level === 'error');
  const warningMessages = messages.filter(msg => msg.level === 'warning');
  const infoMessages = messages.filter(msg => msg.level === 'info');

  return (
    <Box>
      {/* Validation Summary */}
      <Box sx={{ mb: 3, zIndex: 1000 }}>
        {isValid ? (
          <Alert severity="success" icon={<CheckCircle />}>
            <Typography variant="h6">
              ✅ Configuration is Valid!
            </Typography>
            <Typography variant="body2">
              Your configuration passed all validation checks and is ready to use.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="error">
            <Typography variant="h6">
              ❌ Configuration has Issues
            </Typography>
            <Typography variant="body2">
              Please fix the errors below before using this configuration.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<Error />}
          label={`${summary.errors} Errors`}
          color={summary.errors > 0 ? 'error' : 'default'}
          variant={summary.errors > 0 ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<Warning />}
          label={`${summary.warnings} Warnings`}
          color={summary.warnings > 0 ? 'warning' : 'default'}
          variant={summary.warnings > 0 ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<Info />}
          label={`${summary.info} Info`}
          color={summary.info > 0 ? 'info' : 'default'}
          variant={summary.info > 0 ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Error Messages */}
      {errorMessages.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" color="error">
              Errors ({errorMessages.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {errorMessages.map((msg, index) => (
                <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon>
                    {getIcon(msg.level)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Chip
                          label={msg.code}
                          size="small"
                          color="error"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Typography variant="body1" component="span">
                          {msg.message}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {msg.path && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Path: {msg.path}
                          </Typography>
                        )}
                        {msg.suggestion && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            💡 {msg.suggestion}
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Warning Messages */}
      {warningMessages.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" color="warning.main">
              Warnings ({warningMessages.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {warningMessages.map((msg, index) => (
                <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon>
                    {getIcon(msg.level)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Chip
                          label={msg.code}
                          size="small"
                          color="warning"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Typography variant="body1" component="span">
                          {msg.message}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {msg.path && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Path: {msg.path}
                          </Typography>
                        )}
                        {msg.suggestion && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            💡 {msg.suggestion}
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Info Messages */}
      {infoMessages.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" color="info.main">
              Information ({infoMessages.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {infoMessages.map((msg, index) => (
                <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon>
                    {getIcon(msg.level)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Chip
                          label={msg.code}
                          size="small"
                          color="info"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Typography variant="body1" component="span">
                          {msg.message}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {msg.path && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Path: {msg.path}
                          </Typography>
                        )}
                        {msg.suggestion && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            💡 {msg.suggestion}
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Re-validate Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={isValidating ? <CircularProgress size={20} /> : <PlayArrow />}
          onClick={()=> onValidate()}
          disabled={isValidating}
        >
          Re-validate Configuration
        </Button>
      </Box>
    </Box>
  );
};

export default ValidationPanel;
